import { Box } from "@chakra-ui/react"
import { ElementViewEventWrapper } from "packages/acquisition/components/chakra-components/LandingPage/ElementViewEventWrapper"
import { useLandingPageContext } from "packages/acquisition/components/chakra-components/LandingPage/LandingPageProvider"
import { getContentType } from "lib/cms/utils/getContentType"
import dynamic from "next/dynamic"
import React from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useSegment } from "tracking"

const fallbackRender = (error, contentType, id) => {
  // This is to prevent a Pager Duty alert for NextJs cache errors
  const isNextCacheError =
    error.message.includes("Loading chunk") ||
    error.message.includes("Loading CSS chunk")

  if (!isNextCacheError) {
    console.error(
      `Error in CMS component. Content type: ${contentType}. Entry ID: ${id}. Error: ${error.message}`
    )
  }

  return <Box />
}

// Helper type just to keep track of the content models' components we are lazy loading
type LazyLoadedComponentName =
  | "landingPageFaqSection"
  | "LandingPageFaqSection"
  | "landingPageHeroHeadingSection"
  | "LandingPageHeroHeadingSection"
  | "landingPageTitleTextCta"
  | "LandingPageTitleTextCta"
  | "howItWorksNumbers"
  | "HowItWorksNumbers"
  | "landingPageMembershipCardsContainer"
  | "landingPageFootnotes"
  | "LandingPageFootnotes"
  | "heroBlockWithLiveText"
  | "HeroBlockWithLiveText"
  | "generalContentBlockWithLiveText"
  | "GeneralContentBlockWithLiveText"
  | "hero"
  | "Hero"
  | "featuredProducts"
  | "generalLogoContainer"
  | "GeneralLogoContainer"
  | "promoTilesContainer"
  | "PromoTilesContainer"
  | "nmhpCategoryListContainer"
  | "NmhpCategoryListContainer"
  | "loadingComponent"
  | "generalQuoteContainer"
  | "GeneralQuoteContainer"
  | "pageBlogCards"
  | "PageBlogCards"
  | "videoOnDemand"
  | "shopInfluencersContainer"
  | "ShopInfluencersContainer"
  | "heroBlockWithCards"
  | "HeroBlockWithCards"
  | "shopCategoriesContainer"
  | "ShopCategoriesContainer"
  | "pageProfileSection"
  | "PageProfileSection"
  | "pageQuickShop"
  | "PageQuickShop"
  | "pinnedProductCard"
  | "upgradeWithIncentiveBanner"
  | "livePromoTile"
  | "pageTextOnly"
  | "PageTextOnly"
  | "comparisonSlides"
  | "ComparisonSlides"
  | "createableBanner"
  | "CreateableBanner"
  | "PageMembershipOptionsContainer"

type LazyLoadedReturnType = {
  component: React.ComponentType<unknown>
  hasCustomWrapper?: boolean
}

const lazyLoadedComponents: Record<
  LazyLoadedComponentName,
  LazyLoadedReturnType
> = {
  landingPageFaqSection: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/LandingPage/FAQSection"
      ).then((mod) => mod.FAQSection)
    ),
  },
  LandingPageFaqSection: {
    component: dynamic<any>(() =>
      import("packages/acquisition/shared/components/Contentful/FAQs").then(
        (mod) => mod.FAQs
      )
    ),
  },
  landingPageHeroHeadingSection: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/LandingPageHeading"
      ).then((mod) => mod.LandingPageHeading)
    ),
  },
  LandingPageHeroHeadingSection: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/LandingPageHeading"
      ).then((mod) => mod.LandingPageHeading)
    ),
  },
  landingPageTitleTextCta: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/HalfBannerText"
      ).then((mod) => mod.HalfBannerText)
    ),
  },
  LandingPageTitleTextCta: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/HalfBannerText"
      ).then((mod) => mod.HalfBannerText)
    ),
  },
  howItWorksNumbers: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/LandingPage/HowItWorks"
      ).then((mod) => mod.HowItWorks)
    ),
    hasCustomWrapper: true,
  },
  HowItWorksNumbers: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/HowItWorks"
      ).then((mod) => mod.HowItWorks)
    ),
    hasCustomWrapper: true,
  },
  landingPageMembershipCardsContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/LandingPage/LandingPageMemberships"
      ).then((mod) => mod.LandingPageMemberships)
    ),
    hasCustomWrapper: true,
  },
  landingPageFootnotes: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/LandingPage/LandingPageFootnotes"
      ).then((mod) => mod.LandingPageFootnotes)
    ),
  },
  LandingPageFootnotes: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/Footnotes"
      ).then((mod) => mod.Footnotes)
    ),
  },
  heroBlockWithLiveText: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/HeroBlockWithLiveText"
      ).then((mod) => mod.HeroBlockWithLiveText)
    ),
  },
  HeroBlockWithLiveText: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/HeroBlockWithLiveText"
      ).then((mod) => mod.HeroBlockWithLiveText)
    ),
  },
  hero: {
    component: dynamic<any>(() =>
      import("packages/acquisition/shared/components/Contentful/Hero").then(
        (mod) => mod.Hero
      )
    ),
  },
  Hero: {
    component: dynamic<any>(() =>
      import("packages/acquisition/shared/components/Contentful/Hero").then(
        (mod) => mod.Hero
      )
    ),
  },
  generalContentBlockWithLiveText: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/CMS/GeneralContentBlockWithLiveText"
      ).then((mod) => mod.GeneralContentBlockWithLiveText)
    ),
  },
  GeneralContentBlockWithLiveText: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/GeneralContentBlockWithLiveText"
      ).then((mod) => mod.GeneralContentBlockWithLiveTextReduced)
    ),
  },
  featuredProducts: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/CMS/FeaturedProducts"
      ).then((mod) => mod.ProductSuggestions)
    ),
  },
  generalLogoContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/GeneralLogoContainer"
      ).then((mod) => mod.PRLogos)
    ),
  },
  GeneralLogoContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/GeneralLogoContainer"
      ).then((mod) => mod.PRLogos)
    ),
  },
  promoTilesContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/PromoTiles"
      ).then((mod) => mod.PromoTiles)
    ),
  },
  PromoTilesContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/PromoTiles"
      ).then((mod) => mod.PromoTiles)
    ),
  },
  nmhpCategoryListContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/CMS/NmhpCategoryListContainer"
      ).then((mod) => mod.CategoriesCarousel)
    ),
  },
  NmhpCategoryListContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/CategoryListContainer"
      ).then((mod) => mod.CategoryListContainer)
    ),
  },
  loadingComponent: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/LandingPage/ProductRecommendations"
      ).then((mod) => mod.ProductRecommendations)
    ),
  },
  generalQuoteContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/CMS/GeneralQuoteContainer"
      ).then((mod) => mod.QuoteCarousel)
    ),
  },
  GeneralQuoteContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/GeneralQuoteContainer"
      ).then((mod) => mod.GeneralQuoteContainer)
    ),
  },
  pageBlogCards: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/BlogCards"
      ).then((mod) => mod.BlogCards)
    ),
  },
  PageBlogCards: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/BlogCards"
      ).then((mod) => mod.BlogCards)
    ),
  },
  videoOnDemand: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/CMS/VideoOnDemand"
      ).then((mod) => mod.VideoOnDemand)
    ),
  },
  shopInfluencersContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/CMS/ShopInfluencersContainer"
      ).then((mod) => mod.ShopInfluencersContainer)
    ),
  },
  ShopInfluencersContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/ShopInfluencers"
      ).then((mod) => mod.ShopInfluencers)
    ),
  },
  heroBlockWithCards: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/HeroBlocksWithCards"
      ).then((mod) => mod.HeroBlockWithPromoCards)
    ),
  },
  HeroBlockWithCards: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/HeroBlocksWithCards"
      ).then((mod) => mod.HeroBlockWithPromoCards)
    ),
  },
  shopCategoriesContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/ShopCategoriesContainer"
      ).then((mod) => mod.ShopCategoriesContainer)
    ),
  },
  ShopCategoriesContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/ShopCategoriesContainer"
      ).then((mod) => mod.ShopCategoriesContainer)
    ),
  },
  pageProfileSection: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/CMS/Profile"
      ).then((mod) => mod.Profile)
    ),
  },
  PageProfileSection: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/PageProfile"
      ).then((mod) => mod.PageProfile)
    ),
  },
  pageQuickShop: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/CMS/QuickShop"
      ).then((mod) => mod.QuickShopComponent)
    ),
  },
  PageQuickShop: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/QuickShop"
      ).then((mod) => mod.QuickShop)
    ),
  },
  pinnedProductCard: {
    component: dynamic<any>(() =>
      import("packages/acquisition/components/PinnedProduct").then(
        (mod) => mod.PinnedPromoProduct
      )
    ),
  },
  upgradeWithIncentiveBanner: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/UpgradeWithIncentive/components/UpgradeWithIncentiveBanner"
      ).then((mod) => mod.UpgradeWithIncentiveBanner)
    ),
  },
  livePromoTile: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/PromoTiles/LivePromoTile"
      ).then((mod) => mod.LivePromoTile)
    ),
  },
  PageTextOnly: {
    component: dynamic<any>(() =>
      import("packages/acquisition/shared/components/Contentful/TextOnly").then(
        (mod) => mod.TextOnly
      )
    ),
  },
  pageTextOnly: {
    component: dynamic<any>(() =>
      import("packages/acquisition/shared/components/Contentful/TextOnly").then(
        (mod) => mod.TextOnly
      )
    ),
  },
  comparisonSlides: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/ComparisonSlides"
      ).then((mod) => mod.ComparisonSlides)
    ),
  },
  ComparisonSlides: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/ComparisonSlides"
      ).then((mod) => mod.ComparisonSlides)
    ),
  },
  createableBanner: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/CreateableBanner"
      ).then((mod) => mod.CreateableBanner)
    ),
  },
  CreateableBanner: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/CreateableBanner"
      ).then((mod) => mod.CreateableBanner)
    ),
  },
  PageMembershipOptionsContainer: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/MembershipOptions"
      ).then((mod) => mod.MembershipOptions)
    ),
  },
}

// Helper type just to keep track of the content models' components we are previewing
type PreviewLazyLoadedComponentName =
  | LazyLoadedComponentName
  | "categoryList"
  | "promoTile"

export const previewComponents: Record<
  PreviewLazyLoadedComponentName,
  LazyLoadedReturnType
> = {
  ...lazyLoadedComponents,
  categoryList: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/components/chakra-components/CMS/NmhpCategoryListContainer"
      ).then((mod) => mod.CategoryCard)
    ),
  },
  promoTile: {
    component: dynamic<any>(() =>
      import(
        "packages/acquisition/shared/components/Contentful/PromoTiles/PromoTile"
      ).then((mod) => mod.PromoTile)
    ),
  },
}

export const usePageComponents = (
  blocks: unknown[],
  isMember?: boolean
): React.ReactNode[] => {
  const { pageId } = useLandingPageContext()
  const { track } = useSegment()

  return blocks?.map(
    ({ $$contentfulEntry, id, reference, ...fields }: any, i) => {
      const { __typename } = fields
      if (!$$contentfulEntry && !__typename) return null
      const contentType = $$contentfulEntry
        ? getContentType($$contentfulEntry)
        : __typename
      const moduleContent = reference ?? fields?.title

      const trackingModule = {
        modulePosition: i + 1,
        moduleContent: moduleContent ?? "",
        moduleId: `${contentType}-${id}`,
        locationType: contentType,
        pageId,
        track,
      }

      const LazyLoadedComponent = lazyLoadedComponents[contentType]
      const Component = LazyLoadedComponent?.component
        ? React.createElement(LazyLoadedComponent.component, {
            ...fields,
            isMember,
            trackingModule,
          })
        : null

      if (Component) {
        return (
          <ErrorBoundary
            fallbackRender={({ error }) =>
              fallbackRender(error, contentType, id)
            }
            key={id}
          >
            <ElementViewEventWrapper
              trackingModule={trackingModule}
              disabled={LazyLoadedComponent.hasCustomWrapper}
            >
              {Component}
            </ElementViewEventWrapper>
          </ErrorBoundary>
        )
      }

      return null
    }
  )
}
